// Page t("intervenantsPage.title") : camembert CA + KPI + filtres + liste CRUD.

import { FormEvent, useEffect, useMemo, useState } from "react";

import { ApiError } from "../api/client";
import { intervenantsApi } from "../api/intervenants";
import { invoicesApi } from "../api/invoices";
import type {
  IntervenantCreate,
  IntervenantPublic,
  IntervenantUpdate,
  InvoicePublic,
} from "../api/types";
import { useTranslation } from "react-i18next";
import { AppShell } from "../components/AppShell";
import { FormField } from "../components/FormField";
import { Modal } from "../components/Modal";

type DialogState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; intervenant: IntervenantPublic };

// Palette couleurs
const COLORS = [
  "#3b82f6","#10b981","#f59e0b","#ef4444","#8b5cf6",
  "#06b6d4","#f97316","#84cc16","#ec4899","#6366f1",
];

function fmt(amount: number, currency = "EUR") {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
}

// ---- Camembert SVG -------------------------------------------------------

type Slice = { name: string; value: number; color: string; pct: number };

function PieChart({ slices, hovered, onHover }: {
  slices: Slice[];
  hovered: number | null;
  onHover: (i: number | null) => void;
}) {
  const cx = 110, cy = 110, r = 90;
  let angle = -Math.PI / 2;
  const paths = slices.map((s, i) => {
    const sweep = (s.pct / 100) * 2 * Math.PI;
    const x1 = cx + r * Math.cos(angle);
    const y1 = cy + r * Math.sin(angle);
    angle += sweep;
    const x2 = cx + r * Math.cos(angle);
    const y2 = cy + r * Math.sin(angle);
    const large = sweep > Math.PI ? 1 : 0;
    const scale = hovered === i ? 1.06 : 1;
    return {
      d: `M${cx},${cy} L${x1.toFixed(2)},${y1.toFixed(2)} A${r},${r} 0 ${large},1 ${x2.toFixed(2)},${y2.toFixed(2)} Z`,
      color: s.color, scale, i,
    };
  });
  return (
    <svg viewBox="0 0 220 220" className="w-56 h-56 drop-shadow-sm">
      {paths.map(p => (
        <path key={p.i} d={p.d} fill={p.color} stroke="white" strokeWidth="2"
          style={{ transform: `scale(${p.scale})`, transformOrigin: `${cx}px ${cy}px`, transition: "transform 0.15s" }}
          onMouseEnter={() => onHover(p.i)} onMouseLeave={() => onHover(null)}
          className="cursor-pointer" />
      ))}
      <circle cx={cx} cy={cy} r={42} fill="white" />
      <text x={cx} y={cy - 6} textAnchor="middle" fontSize="11" fill="#64748b">Total</text>
      <text x={cx} y={cy + 12} textAnchor="middle" fontSize="12" fontWeight="bold" fill="#1e293b">
        {fmt(slices.reduce((s, sl) => s + sl.value, 0))}
      </text>
    </svg>
  );
}

export function IntervenantsPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<IntervenantPublic[] | null>(null);
  const [invoices, setInvoices] = useState<InvoicePublic[]>([]);
  const [activeOnly, setActiveOnly] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>({ mode: "closed" });
  const [hovered, setHovered] = useState<number | null>(null);
  // Filtres camembert
  const [filterPieYear, setFilterPieYear] = useState<string>("");
  const [filterPieMonth, setFilterPieMonth] = useState<string>("");
  // Analyse mensuelle
  const [filterIntId, setFilterIntId] = useState<string>("");
  const [filterMonth, setFilterMonth] = useState<string>("");

  // Années dispo pour le camembert
  const pieYears = useMemo(() => {
    const s = new Set<string>();
    for (const inv of invoices) s.add((inv.issue_date as string).slice(0, 4));
    return Array.from(s).sort().reverse();
  }, [invoices]);

  // Mois dispo pour le camembert (filtrés par année)
  const pieMonths = useMemo(() => {
    const s = new Set<string>();
    for (const inv of invoices) {
      const m = (inv.issue_date as string).slice(0, 7);
      if (!filterPieYear || m.startsWith(filterPieYear)) s.add(m);
    }
    return Array.from(s).sort().reverse();
  }, [invoices, filterPieYear]);

  // Slices camembert — CA par intervenant filtré
  const pieSlices: Slice[] = useMemo(() => {
    const map = new Map<string, number>();
    for (const inv of invoices) {
      if (!inv.intervenant_id) continue;
      const d = inv.issue_date as string;
      if (filterPieYear && !d.startsWith(filterPieYear)) continue;
      if (filterPieMonth && !d.startsWith(filterPieMonth)) continue;
      const name = (items ?? []).find(i => i.id === inv.intervenant_id)?.name ?? inv.intervenant_id ?? "";
      const ttc = parseFloat(inv.amount_ttc as unknown as string) || 0;
      map.set(name, (map.get(name) ?? 0) + ttc);
    }
    const total = Array.from(map.values()).reduce((s, v) => s + v, 0);
    if (total === 0) return [];
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, value], i) => ({
        name, value, color: COLORS[i % COLORS.length],
        pct: (value / total) * 100,
      }));
  }, [invoices, items, filterPieYear, filterPieMonth]);

  const activeSlice = hovered !== null ? pieSlices[hovered] : null;

  // KPI globaux
  const kpi = useMemo(() => {
    const actifs = (items ?? []).filter(i => i.is_active).length;
    const inactifs = (items ?? []).length - actifs;
    const totalCA = invoices.reduce((s, i) => s + (parseFloat(i.amount_ttc as unknown as string) || 0), 0);
    const totalPaid = invoices.filter(i => i.status === "paid").reduce((s, i) => s + (parseFloat(i.amount_ttc as unknown as string) || 0), 0);
    return { actifs, inactifs, totalCA, totalPaid };
  }, [items, invoices]);

  // Factures par intervenant
  const invoicesByInt = useMemo(() => {
    const map = new Map<string, { total: number; paid: number; pending: number; nb: number }>();
    for (const inv of invoices) {
      if (!inv.intervenant_id) continue;
      const ttc = parseFloat(inv.amount_ttc as unknown as string) || 0;
      const cur = map.get(inv.intervenant_id) ?? { total: 0, paid: 0, pending: 0, nb: 0 };
      cur.total += ttc;
      if (inv.status === "paid") cur.paid += ttc;
      if (inv.status === "issued" || inv.status === "overdue") cur.pending += ttc;
      cur.nb += 1;
      map.set(inv.intervenant_id, cur);
    }
    return map;
  }, [invoices]);

  // Mois disponibles dans les factures
  const availableMonths = useMemo(() => {
    const set = new Set<string>();
    for (const inv of invoices) {
      if (!inv.issue_date) continue;
      set.add((inv.issue_date as string).slice(0, 7));
    }
    return Array.from(set).sort().reverse();
  }, [invoices]);

  // Données pour l'analyse mensuelle (filtrées par intervenant + mois)
  const monthlyData = useMemo(() => {
    const rows: { month: string; name: string; nb: number; total: number; paid: number; pending: number }[] = [];
    const filtered = invoices.filter(inv => {
      if (!inv.intervenant_id) return false;
      if (filterIntId && inv.intervenant_id !== filterIntId) return false;
      if (filterMonth && !(inv.issue_date as string).startsWith(filterMonth)) return false;
      return true;
    });
    const map = new Map<string, { month: string; name: string; nb: number; total: number; paid: number; pending: number }>();
    for (const inv of filtered) {
      const month = (inv.issue_date as string).slice(0, 7);
      const intName = items?.find(i => i.id === inv.intervenant_id)?.name ?? inv.intervenant_id ?? "";
      const key = month + "|" + inv.intervenant_id;
      const ttc = parseFloat(inv.amount_ttc as unknown as string) || 0;
      const cur = map.get(key) ?? { month, name: intName, nb: 0, total: 0, paid: 0, pending: 0 };
      cur.nb += 1;
      cur.total += ttc;
      if (inv.status === "paid") cur.paid += ttc;
      if (inv.status === "issued" || inv.status === "overdue") cur.pending += ttc;
      map.set(key, cur);
    }
    for (const v of map.values()) rows.push(v);
    return rows.sort((a, b) => b.month.localeCompare(a.month) || b.total - a.total);
  }, [invoices, filterIntId, filterMonth, items]);

  async function reload() {
    setListError(null);
    try {
      const [data, inv] = await Promise.all([
        intervenantsApi.list(activeOnly ? { active_only: true } : {}),
        invoicesApi.list({ limit: 200 }),
      ]);
      setItems(data);
      setInvoices(inv.items ?? (inv as unknown as InvoicePublic[]));
    } catch (err) {
      setListError(err instanceof Error ? err.message : "Erreur");
      setItems([]);
    }
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOnly]);

  async function onDelete(i: IntervenantPublic) {
    if (!window.confirm(t("intervenantsPage.deleteConfirm").replace("{name}", i.name))) return;
    try {
      await intervenantsApi.delete(i.id);
      await reload();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Suppression impossible";
      window.alert(msg);
    }
  }

  return (
    <AppShell>
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{t("intervenantsPage.title")}</h1>
          <p className="text-sm text-slate-500">{t("intervenantsPage.subtitle")}</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setDialog({ mode: "create" })}>
          + {t("intervenantsPage.newIntervenant")}
        </button>
      </header>

      {/* CAMEMBERT */}
      {pieSlices.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <h2 className="text-base font-semibold text-slate-800 mr-auto">
              {t("intervenantsPage.caByIntervenant")}
            </h2>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">{t("common.year")}</label>
              <select
                value={filterPieYear}
                onChange={(e) => { setFilterPieYear(e.target.value); setFilterPieMonth(""); }}
                className="input text-sm py-1 min-w-[100px]"
              >
                <option value="">{t("common.allYears")}</option>
                {pieYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">{t("common.month")}</label>
              <select
                value={filterPieMonth}
                onChange={(e) => setFilterPieMonth(e.target.value)}
                className="input text-sm py-1 min-w-[130px]"
              >
                <option value="">{t("intervenantsPage.allIntervenants")}</option>
                {pieMonths.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            {(filterPieYear || filterPieMonth) && (
              <button
                type="button"
                className="text-xs text-slate-500 hover:text-slate-800 underline"
                onClick={() => { setFilterPieYear(""); setFilterPieMonth(""); }}
              >
                {t("common.reset")}
              </button>
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-8">
            <div className="relative flex-shrink-0">
              <PieChart slices={pieSlices} hovered={hovered} onHover={setHovered} />
              {activeSlice && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="bg-white/95 rounded-lg px-3 py-2 shadow-lg text-center border border-slate-100 mt-10">
                    <p className="text-xs text-slate-500 truncate max-w-[130px]">{activeSlice.name}</p>
                    <p className="text-sm font-bold text-slate-900">{fmt(activeSlice.value)}</p>
                    <p className="text-xs text-slate-400">{activeSlice.pct.toFixed(1)} %</p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
              {pieSlices.map((s, i) => (
                <div
                  key={s.name}
                  className={[
                    "flex items-center gap-3 rounded-lg px-3 py-2 cursor-pointer transition-colors",
                    hovered === i ? "bg-slate-100" : "hover:bg-slate-50",
                  ].join(" ")}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{s.name}</p>
                    <p className="text-xs text-slate-500">{fmt(s.value)} &middot; {s.pct.toFixed(1)} %</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* KPI GLOBAUX */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs text-slate-500 mb-1">{t("intervenantsPage.active")}</p>
          <p className="text-2xl font-bold text-slate-900">{kpi.actifs}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs text-slate-500 mb-1">{t("intervenantsPage.inactive")}</p>
          <p className="text-2xl font-bold text-slate-400">{kpi.inactifs}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs text-slate-500 mb-1">{t("intervenantsPage.caGenerated")}</p>
          <p className="text-2xl font-bold text-slate-900">{fmt(kpi.totalCA)}</p>
        </div>
        <div className="bg-white border border-emerald-200 rounded-xl p-4">
          <p className="text-xs text-emerald-600 mb-1">{t("intervenantsPage.caPaid")}</p>
          <p className="text-2xl font-bold text-emerald-700">{fmt(kpi.totalPaid)}</p>
        </div>
      </div>

      <label className="mb-4 inline-flex items-center gap-2 text-sm text-slate-600">
        <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
        {t("intervenantsPage.activeOnly")}
      </label>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {listError && <p role="alert" className="p-4 text-sm text-red-600">{listError}</p>}
        {items === null && <p className="p-6 text-sm text-slate-500">{t("common.loading")}</p>}
        {items !== null && items.length === 0 && !listError && (
          <p className="p-6 text-sm text-slate-500">{t("intervenantsPage.noIntervenants")}</p>
        )}
        {items && items.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">{t("common.name")}</th>
                <th className="px-4 py-2 font-medium">Rôle</th>
                <th className="px-4 py-2 font-medium">Statut</th>
                <th className="px-4 py-2 font-medium text-right">{t("intervenantsPage.nbInvoices")}</th>
                <th className="px-4 py-2 font-medium text-right">{t("intervenantsPage.caTotal")}</th>
                <th className="px-4 py-2 font-medium text-right">{t("intervenantsPage.collected")}</th>
                <th className="px-4 py-2 font-medium text-right">{t("intervenantsPage.pending")}</th>
                <th className="px-4 py-2 font-medium w-32 text-right">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => {
                const s = invoicesByInt.get(i.id) ?? { total: 0, paid: 0, pending: 0, nb: 0 };
                return (
                  <tr key={i.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2 font-medium text-slate-900">{i.name}</td>
                    <td className="px-4 py-2 text-slate-600">{i.role ?? "—"}</td>
                    <td className="px-4 py-2">
                      {i.is_active
                        ? <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700">actif</span>
                        : <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">inactif</span>}
                    </td>
                    <td className="px-4 py-2 text-right text-slate-700">{s.nb || "—"}</td>
                    <td className="px-4 py-2 text-right font-semibold text-slate-800">{s.total ? fmt(s.total) : "—"}</td>
                    <td className="px-4 py-2 text-right text-emerald-700">{s.paid ? fmt(s.paid) : "—"}</td>
                    <td className="px-4 py-2 text-right text-amber-700">{s.pending ? fmt(s.pending) : "—"}</td>
                    <td className="px-4 py-2 text-right space-x-3">
                      <button type="button" className="text-brand-600 hover:underline" onClick={() => setDialog({ mode: "edit", intervenant: i })}>{t("common.edit")}</button>
                      <button type="button" className="text-red-600 hover:underline" onClick={() => onDelete(i)}>{t("common.delete")}</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ANALYSE MENSUELLE */}
      <section className="mt-8 bg-white border border-slate-200 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">{t("intervenantsPage.monthlyAnalysis")}</h2>
        <div className="flex flex-wrap gap-3 mb-5">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500">{t("common.intervenant")}</label>
            <select
              value={filterIntId}
              onChange={(e) => setFilterIntId(e.target.value)}
              className="input text-sm min-w-[180px]"
            >
              <option value="">{t("intervenantsPage.allIntervenants")}</option>
              {(items ?? []).map((i) => (
                <option key={i.id} value={i.id}>{i.name}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500">{t("common.month")}</label>
            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="input text-sm min-w-[150px]"
            >
              <option value="">{t("intervenantsPage.allMonths")}</option>
              {availableMonths.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
          {(filterIntId || filterMonth) && (
            <div className="flex items-end">
              <button
                type="button"
                className="text-sm text-slate-500 hover:text-slate-800 underline"
                onClick={() => { setFilterIntId(""); setFilterMonth(""); }}
              >
                {t("common.reset")}
              </button>
            </div>
          )}
        </div>

        {monthlyData.length === 0 ? (
          <p className="text-sm text-slate-400 italic">{t("common.noData")}</p>
        ) : (
          <>
            {/* Mini KPI de la sélection */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">{t("intervenantsPage.nbInvoices")}</p>
                <p className="text-xl font-bold text-slate-800">{monthlyData.reduce((s, r) => s + r.nb, 0)}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">{t("intervenantsPage.caTotal")}</p>
                <p className="text-xl font-bold text-slate-800">{fmt(monthlyData.reduce((s, r) => s + r.total, 0))}</p>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3">
                <p className="text-xs text-emerald-600">{t("intervenantsPage.collected")}</p>
                <p className="text-xl font-bold text-emerald-700">{fmt(monthlyData.reduce((s, r) => s + r.paid, 0))}</p>
              </div>
            </div>

            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2">{t("common.month")}</th>
                  <th className="px-3 py-2">{t("common.intervenant")}</th>
                  <th className="px-3 py-2 text-right">{t("intervenantsPage.nbInvoices")}</th>
                  <th className="px-3 py-2 text-right">{t("intervenantsPage.caTotal")}</th>
                  <th className="px-3 py-2 text-right">{t("intervenantsPage.collected")}</th>
                  <th className="px-3 py-2 text-right">{t("intervenantsPage.pending")}</th>
                </tr>
              </thead>
              <tbody>
                {monthlyData.map((row, idx) => (
                  <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-slate-700">{row.month}</td>
                    <td className="px-3 py-2 font-medium text-slate-900">{row.name}</td>
                    <td className="px-3 py-2 text-right text-slate-600">{row.nb}</td>
                    <td className="px-3 py-2 text-right font-semibold text-slate-800">{fmt(row.total)}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">{row.paid ? fmt(row.paid) : "—"}</td>
                    <td className="px-3 py-2 text-right text-amber-700">{row.pending ? fmt(row.pending) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>

      <IntervenantDialog
        state={dialog}
        onClose={() => setDialog({ mode: "closed" })}
        onSaved={async () => { setDialog({ mode: "closed" }); await reload(); }}
      />
    </AppShell>
  );
}

function IntervenantDialog({ state, onClose, onSaved }: {
  state: DialogState; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isOpen = state.mode !== "closed";
  const [values, setValues] = useState<IntervenantCreate>({ name: "", email: null, role: null, is_active: true });
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (state.mode === "edit") {
      setValues({ name: state.intervenant.name, email: state.intervenant.email, role: state.intervenant.role, is_active: state.intervenant.is_active });
    } else if (state.mode === "create") {
      setValues({ name: "", email: null, role: null, is_active: true });
    }
    setError(null);
  }, [state]);

  if (!isOpen) return null;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      const payload: IntervenantCreate | IntervenantUpdate = {
        name: values.name.trim(), email: nullIfEmpty(values.email),
        role: nullIfEmpty(values.role), is_active: values.is_active,
      };
      if (state.mode === "edit") await intervenantsApi.update(state.intervenant.id, payload);
      else await intervenantsApi.create(payload as IntervenantCreate);
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail);
      else setError("Impossible d'enregistrer");
    } finally {
      setPending(false);
    }
  }

  const title = state.mode === "edit" ? t("intervenantsPage.editIntervenant") : t("intervenantsPage.newIntervenantTitle");

  return (
    <Modal open={isOpen} onClose={onClose} title={title}>
      <form onSubmit={onSubmit} className="space-y-4" aria-label="intervenant-form">
        <FormField label={t("common.name")} htmlFor="name" required>
          <input id="name" required value={values.name} onChange={(e) => setValues({ ...values, name: e.target.value })} className="input" />
        </FormField>
        <FormField label={t("common.email")} htmlFor="email">
          <input id="email" type="email" value={values.email ?? ""} onChange={(e) => setValues({ ...values, email: e.target.value || null })} className="input" />
        </FormField>
        <FormField label="Rôle" htmlFor="role" hint="Ex: Consultant, Dev…">
          <input id="role" value={values.role ?? ""} onChange={(e) => setValues({ ...values, role: e.target.value || null })} className="input" />
        </FormField>
        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={values.is_active ?? true} onChange={(e) => setValues({ ...values, is_active: e.target.checked })} />
          Actif
        </label>
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" className="text-slate-500 hover:text-slate-800 text-sm" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" disabled={pending} className="btn-primary">{pending ? t("common.saving") : t("common.save")}</button>
        </div>
      </form>
    </Modal>
  );
}

function nullIfEmpty(v: string | null | undefined): string | null {
  const t = (v ?? "").trim();
  return t ? t : null;
}
